import { createFileRoute, Link } from '@tanstack/react-router'

import { useAuth } from '~/auth/auth-context'
import { ChevronLeftIcon } from '~/components/icons'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { TrendChart } from '~/components/trend-chart'
import { useNutrition } from '~/data/nutrition-store'
import { todayKey } from '~/data/weighins'
import { useWeighIns } from '~/data/weighins-store'
import { dayStart, formatDayShort } from '~/lib/dates'
import { aimSaid } from '~/lib/goal-aim'
import { withoutDialog } from '~/lib/dialogs'
import { goalsGoneBy, type GoneBy } from '~/lib/goals-gone-by'
import { trendOf } from '~/lib/metric-series'
import { formatWeight } from '~/lib/units'
import type { Units } from '~/data/types'

export const Route = createFileRoute('/you_/goals-gone-by')({
  component: GoalsGoneByPage,
})

/**
 * The body goals gone by — the nutrition spec, The goal itself. Every one a later goal
 * replaced and every one ended, newest first.
 *
 * Read here and nowhere else: nothing on this page opens, changes or removes a goal. It is a
 * record of what was aimed at rather than something still in play, and each one says what the
 * weight actually did over it rather than what it asked of a day — the targets a goal set in
 * February wanted say nothing about whether February worked.
 */
function GoalsGoneByPage() {
  const data = useNutrition()
  const weighIns = useWeighIns()
  const { units } = useAuth()
  const today = todayKey()

  if (data === null || weighIns === null) {
    return (
      <SkeletonBlock label="Loading the goals gone by" className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </SkeletonBlock>
    )
  }

  const trend = trendOf(
    [...weighIns]
      .filter((weighIn) => weighIn.weightKg !== null)
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((weighIn) => ({ day: weighIn.day, value: weighIn.weightKg ?? 0 })),
  )
  const gone = goalsGoneBy(data.goals, trend, today)

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-2">
        <Link
          to="/you"
          search={withoutDialog}
          aria-label="Back to you"
          className="grid size-9 shrink-0 place-items-center rounded-full text-ink-muted transition-colors hover:bg-hover hover:text-ink"
        >
          <ChevronLeftIcon className="size-6" />
        </Link>
        <h1 className="text-2xl font-semibold">Goals gone by</h1>
      </div>

      {gone.length === 0 ? (
        <p className="text-sm text-ink-muted">No goal has gone by yet.</p>
      ) : (
        <ul aria-label="Goals gone by" className="space-y-3">
          {gone.map((each) => (
            <GoneByCard key={each.goal.id} gone={each} units={units} />
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * One goal gone by: what it aimed at, the stretch it stood over, and what the weight did —
 * the trend at each end with the trend between them drawn as a line. A stretch nobody weighed
 * themselves across names no weights and draws no line, rather than drawing a flat one.
 */
function GoneByCard({ gone, units }: { gone: GoneBy; units: Units }) {
  const stretch = `${formatDayShort(dayStart(gone.from))} – ${formatDayShort(dayStart(gone.to))}`
  const moved = gone.fromKg !== null && gone.toKg !== null ? gone.toKg - gone.fromKg : null

  return (
    <li className="relative overflow-hidden rounded-2xl bg-surface-raised p-5 ring-1 ring-white/10">
      {gone.line.length > 0 && (
        <TrendChart
          points={gone.line}
          label={`The trend weight over ${aimSaid(gone.goal, units)}`}
          curveBand={0.7}
          className="pointer-events-none absolute inset-x-0 bottom-0 h-20"
        />
      )}
      <div className="relative">
        <p className="text-lg font-medium">{aimSaid(gone.goal, units)}</p>
        <p className="text-sm text-ink-muted">
          {stretch}
          {gone.goal.endedOn !== null && ' · ended early'}
        </p>
        {gone.fromKg === null || gone.toKg === null ? (
          <p className="mt-3 text-sm text-ink-muted">Nothing was weighed over this one.</p>
        ) : (
          <dl className="mt-3 flex items-baseline gap-4 text-sm">
            <div className="min-w-0">
              <dt className="truncate text-xs text-ink-muted">Started at</dt>
              <dd className="truncate font-medium">{formatWeight(gone.fromKg, units)}</dd>
            </div>
            <div className="min-w-0">
              <dt className="truncate text-xs text-ink-muted">Ended at</dt>
              <dd className="truncate font-medium">{formatWeight(gone.toKg, units)}</dd>
            </div>
            {moved !== null && Math.abs(moved) >= 0.05 && (
              <div className="min-w-0">
                <dt className="truncate text-xs text-ink-muted">{moved < 0 ? 'Lost' : 'Gained'}</dt>
                <dd className="truncate font-medium">{formatWeight(Math.abs(moved), units)}</dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </li>
  )
}
